// api/imageBlobCache.ts
//
// Authenticated image loading for ownership-checked / encrypted endpoints
// (GET /v1/images/*). A plain <img src> cannot authenticate cross-origin
// (the auth cookie is dropped on cross-site subresource requests → 401 → ORB
// block), so we fetch with the Bearer token and hand the result to the DOM as
// a blob: URL.
//
// This module owns the blob: URL lifecycle for the whole app:
//   - in-flight de-duplication (the same image is fetched once)
//   - reference counting (a blob is never revoked while a component shows it)
//   - LRU eviction of idle entries (bounded memory over long sessions)
import { buildHeaders } from './config';
import { imageUrl } from './endpoints';

/** Max number of idle (refCount === 0) entries kept before LRU eviction. */
const MAX_IDLE_ENTRIES = 50;

interface CacheEntry {
  /** The object URL, set once the fetch resolves. */
  url?: string;
  /** Shared promise so concurrent acquirers de-duplicate the fetch. */
  promise: Promise<string>;
  /** Number of live consumers currently displaying this image. */
  refCount: number;
  /** Timestamp of the last acquire/release, used for LRU ordering. */
  lastUsed: number;
}

const cache = new Map<string, CacheEntry>();

/** Resolve the absolute request URL for an image path/hash (reuses imageUrl). */
function resolveUrl(path: string): string {
  return imageUrl(path);
}

/** Fetch an image with auth and return its Blob. Throws on non-OK responses. */
export async function fetchImageBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(resolveUrl(path), {
    method: 'GET',
    headers: buildHeaders(),
    // Send the httpOnly media-session cookie too, so either auth path works.
    credentials: 'include',
    signal,
  });
  if (!res.ok) {
    throw new Error(`Image fetch failed (${res.status}) for ${path}`);
  }
  return res.blob();
}

/** Revoke and drop a single entry. */
function evictEntry(key: string, entry: CacheEntry): void {
  if (entry.url) URL.revokeObjectURL(entry.url);
  cache.delete(key);
}

/**
 * Evict idle (refCount === 0) entries beyond MAX_IDLE_ENTRIES, oldest first.
 * Entries that are still referenced are never evicted.
 */
function evictIdleIfNeeded(): void {
  const idle = [...cache.entries()]
    .filter(([, e]) => e.refCount === 0)
    .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

  const excess = idle.length - MAX_IDLE_ENTRIES;
  for (let i = 0; i < excess; i++) {
    const [key, entry] = idle[i];
    evictEntry(key, entry);
  }
}

/**
 * Acquire a blob: URL for an image path. Increments the reference count; the
 * caller MUST pair every successful acquire with a release(path).
 *
 * Concurrent acquirers of the same path share one fetch. The optional signal
 * only aborts THIS caller's wait — the underlying fetch is shared and is not
 * cancelled while other acquirers are still waiting on it.
 */
export async function acquire(path: string, signal?: AbortSignal): Promise<string> {
  if (!path) throw new Error('acquire: empty image path');

  let entry = cache.get(path);

  if (!entry) {
    // First requester: kick off the fetch. Use an unsignalled fetch so the
    // shared result isn't tied to one caller's lifecycle.
    const promise = fetchImageBlob(path).then((blob) => {
      const created = URL.createObjectURL(blob);
      const e = cache.get(path);
      if (e) e.url = created;
      return created;
    });
    entry = { promise, refCount: 0, lastUsed: Date.now() };
    cache.set(path, entry);

    // Drop the entry if the very first fetch fails, so a retry can happen.
    promise.catch(() => {
      const e = cache.get(path);
      if (e?.refCount === 0 && !e.url) cache.delete(path);
    });
  }

  entry.refCount += 1;
  entry.lastUsed = Date.now();
  evictIdleIfNeeded();

  if (signal) {
    const activeEntry = entry;
    return new Promise<string>((resolve, reject) => {
      const onAbort = () => { reject(new DOMException('Aborted', 'AbortError')); };
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
      activeEntry.promise.then(
        (url) => {
          signal.removeEventListener('abort', onAbort);
          resolve(url);
        },
        (error: unknown) => {
          signal.removeEventListener('abort', onAbort);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  }

  return entry.promise;
}

/**
 * Release a previously acquired image path. Decrements the reference count.
 * The blob: URL is kept (for reuse) until LRU eviction; it is NOT revoked here.
 */
export function release(path: string): void {
  const entry = cache.get(path);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  entry.lastUsed = Date.now();
  // The freshly-released entry is the newest, so eviction here trims older
  // idle entries first and enforces a true idle cap.
  if (entry.refCount === 0) evictIdleIfNeeded();
}

/** Test/diagnostics helper: revoke everything and clear the cache. */
export function _resetImageBlobCache(): void {
  for (const [key, entry] of cache.entries()) evictEntry(key, entry);
}

/** Test/diagnostics helper: current cache size. */
export function _imageBlobCacheSize(): number {
  return cache.size;
}
